<script lang="ts" setup>
import { computed, ref, onMounted, watch } from 'vue'
import type { Props, DataTrack } from './types'
import InvestmentProjectCard from '@/components/InvestmentProjectCard/index.vue'
import { extractListingIdsFromProjectsSummaries } from '@/composables/useOnboarding/utils'

import './_index.scss'

const { t } = useI18n()

const props = withDefaults(defineProps<Props>(), {
  numRecommendations: 10,
  title: '',
  listingId: '',
  businessHubs: '',
  projects: () => [],
  screen: 'pdp',
  isBrochure: false
})

const title = computed(() => {
  return props.title || t('title')
})

const slider = ref<HTMLElement | null>()
const sliders = ref()

const sliderScrollLeft = ref(0)
const scrollLeftLimit = ref(0)

const recommendations = computed(() => {
  if (!props.projects?.length) {
    return []
  }

  const filteredProjects = props.projects.filter((project: any) => {
    return project.code !== props.listingId && project.business_hub === props.businessHubs
  })
  const limit = sliderScrollLeft.value > 0 ? props.numRecommendations : 4
  return filteredProjects.slice(0, limit)
})

const getCardWidth = ():number => {
  return sliders.value.children[0].offsetWidth + 40
}

const scrollSlider = () => {
  slider.value!.scroll({
    behavior: 'smooth',
    left: sliderScrollLeft.value
  })
}

const moveLeft = () => {
  const newScrollLeft = sliderScrollLeft.value - getCardWidth() - 40

  sliderScrollLeft.value = newScrollLeft < 0 ? 0 : newScrollLeft
  scrollSlider()
}

const moveRight = () => {
  const cardWidth = getCardWidth()
  const slidesContainerWidth = scrollLeftLimit.value
  const newScrollLeft = sliderScrollLeft.value + cardWidth

  sliderScrollLeft.value = newScrollLeft > slidesContainerWidth ? slidesContainerWidth : newScrollLeft
  scrollSlider()
}

const handleScroll = (event: Event) => {
  const target = event.target as HTMLElement
  const scrollAmount = target.scrollLeft
  sliderScrollLeft.value = scrollAmount
}
const collectionId = ref<string>(`carousel-recommendations-pdp-${crypto.randomUUID()}`)

const trackCollectionData = () => {
  const listings = extractListingIdsFromProjectsSummaries(recommendations.value)
  const data = {
    collection_id: collectionId.value,
    description: 'PDPcarousel showing projects filtered by business hubs',
    collection_name: 'PDP by Business Hubs',
    screen: props.screen,
    listings,
    collection_criteria: [{
      field: 'fFilter by recommendations of projects different from the current one but within the same business hubs',
      value: props.businessHubs,
      operator: 'equal'
    }]
  }

  trackListingCollectionViewed(data)
}

const trackListingCollectionViewed = (data: DataTrack): void => {
  const { $trackEvent } = useNuxtApp()
  $trackEvent({
    name: 'listing_collection_viewed',
    version: '1-0-0',
    data
  })
}

onMounted(() => {
  scrollLeftLimit.value = slider.value!.scrollWidth - slider.value!.clientWidth
  trackCollectionData()
})

watch(sliderScrollLeft, () => {
  scrollLeftLimit.value = slider.value!.scrollWidth - slider.value!.clientWidth
})

const COOKIE_EXPIRATION = 60 * 10

const handleCardClick = (cardIndex: number, collectionId: string | undefined, projectCode: string | undefined): void => {
  const cookieName = `listingCollection_last_card_${projectCode}`
  const collectionCookie = useCookie(cookieName, { maxAge: COOKIE_EXPIRATION })

  collectionCookie.value = {
    collection_id: collectionId,
    index: cardIndex
  }
}

</script>

<script lang="ts">
export default {
  name: 'RecommendationsCarousel'
}
</script>

<template>
  <section>
    <div class="recommendations-carousel mb-56">
      <h2
        v-if="!isBrochure"
        class="pt-40 pb-16 text-22 lg:text-32 md:leading-10 font-medium">
        {{ title }}
      </h2>

      <div class="recommendations-carousel__holder flex items-center justify-center">
        <ClientOnly>
          <div
            v-if="recommendations.length >= 3"
            class="hidden lg:inline">
            <button
              id="recommendations-carousel__slider-left"
              data-lh-id="pd-button-carousel-left"
              class="recommendations-carousel__slider-button"
              :disabled="sliderScrollLeft === 0"
              aria-label="Atrás"
              @click="moveLeft">
              <span class="w-32 h-32">
                <NuxtIsland name="IconChevronLeft" />
              </span>
            </button>
          </div>
        </ClientOnly>

        <div
          id="recommendations-carousel__slider"
          ref="slider"
          class="overflow-x-auto pl-24 lg:pl-0 scrollbar-hidden"
          :class="{ 'w-screen px-0': isBrochure }"
          :data-lh-id="`${screen}-recommendations-slider`"
          @scroll="handleScroll">
          <div
            ref="sliders"
            class="flex gap-16">
            <InvestmentProjectCard
              v-for="(project, index) in recommendations"
              :key="project.code"
              :project="project"
              :site="screen"
              :is-brochure="isBrochure"
              clickable
              @card-click="handleCardClick(index, collectionId, project.code )" />

            <span class="block md:hidden w-1 flex-none" />
          </div>
        </div>

        <ClientOnly>
          <div
            v-if="recommendations.length >= 3"
            class="hidden lg:inline">
            <button
              id="recommendations-carousel__slider-right"
              class="recommendations-carousel__slider-button"
              data-lh-id="pd-button-carousel-right"
              :disabled="sliderScrollLeft === scrollLeftLimit"
              aria-label="Siguiente"
              @click="moveRight">
              <span class="w-32 h-32">
                <NuxtIsland name="IconChevronRight" />
              </span>
            </button>
          </div>
        </ClientOnly>
      </div>
    </div>
  </section>
</template>

<i18n src="./i18n.json" lang="json"></i18n>
